/**
 * Compact user menu — used in the dashboard top edge and the desk top bar.
 *
 * Renders nothing while session is loading (avoids a flash of "anonymous"
 * before the cookie cache resolves). Renders nothing if there's no
 * session at all (the router guard handles redirecting to /login).
 *
 * Keep this lean: avatar (or initial fallback), first name, sign-out
 * trigger. Anything beyond that — switch account, account settings, plan
 * — gets added when there's a real reason to.
 */

import { LogOut } from "lucide-react";
import { useState } from "react";
import { signOut, useSession } from "@/api/auth-client";

export function UserMenu({ compact = false }: { compact?: boolean }) {
  const { data: session, isPending } = useSession();
  const [signingOut, setSigningOut] = useState(false);
  // Tracks whether the avatar img errored. Some browsers reject
  // googleusercontent.com requests (referrer-policy plugins, ad blockers,
  // CSP overrides). When that happens we fall back to the initial badge
  // — same width/height so the layout doesn't shift.
  const [imgFailed, setImgFailed] = useState(false);

  if (isPending || !session?.user) return null;

  const user = session.user;
  // Better Auth's user type has name + image + email. We may have hooked
  // additional fields (e.g. spend_limit), but those aren't on the
  // session payload by default.
  const firstName = user.name?.trim().split(/\s+/)[0] ?? "";
  const initial = firstName ? firstName[0]!.toUpperCase() : "·";
  const showImage = user.image && !imgFailed;

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      // Better Auth sets the cookie to expire and the next useSession
      // tick yields no data, which the router guard sees and redirects
      // to /login. We don't navigate manually — that races with state
      // updates and can flash the dashboard mid-redirect.
    } catch {
      setSigningOut(false);
    }
  };

  return (
    <div
      className="flex items-center gap-2"
      title={user.email ?? undefined}
      style={{ fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)" }}
    >
      {showImage ? (
        <img
          src={user.image as string}
          alt=""
          // Ask the browser not to send Referer to googleusercontent.com.
          // Google's image CDN doesn't require it, and some browsers /
          // privacy extensions block cross-origin loads when the Referer
          // identifies a private localhost origin. no-referrer is what
          // Google's own products use.
          referrerPolicy="no-referrer"
          width={20}
          height={20}
          style={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            objectFit: "cover",
            display: "block",
            flexShrink: 0,
          }}
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span
          aria-hidden="true"
          className="flex items-center justify-center"
          style={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "var(--accent-light)",
            color: "var(--accent)",
            fontWeight: 600,
            fontSize: "var(--text-2xs)",
            flexShrink: 0,
          }}
        >
          {initial}
        </span>
      )}
      {!compact && (
        <span
          style={{
            color: "var(--t3)",
            maxWidth: 100,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {firstName || user.email}
        </span>
      )}
      <button
        type="button"
        onClick={handleSignOut}
        disabled={signingOut}
        title={`Sign out${user.email ? ` (${user.email})` : ""}`}
        aria-label="Sign out"
        className="p-1 rounded hover:bg-[var(--hover)] transition-colors disabled:opacity-60"
        style={{ color: "var(--t4)" }}
      >
        <LogOut size={13} />
      </button>
    </div>
  );
}
